/* eslint-disable react-hooks/exhaustive-deps */
import { useNavigate, useParams } from "react-router";
import {
  ControlledInput,
  ControlledSelect,
  FormUpload,
  PrimaryButton,
  SecondaryButton,
} from "../../components";
import { withProtectedRoute } from "../../utils/HOC/withProtectedRoute";
import { useForm } from "react-hook-form";
import { SketchPicker } from "react-color";
import { useEffect, useState } from "react";
import { useBadges } from "../../store/useBadges";
import { useMarketPricing } from "../../store/useMarketPricing";
import { useUploadImage } from "../../store/useUploadImage";
import { useAlert } from "../../store/useAlert";

type Inputs = {
  name: string;
  description: string;
  minDrops: number;
  maxDrops: number;
  type: string;
};

function EditBadgeScreen() {
  /** Hooks */
  const params = useParams();
  const navigate = useNavigate();
  const { setAlert } = useAlert();
  const {
    getOneBadge,
    oneBadge,
    updateBadge,
    updatedBadge,
    clearUpdatedBadge,
  } = useBadges();
  const { getAllMarketPrices, marketPrices } = useMarketPricing();
  const {
    reset,
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<Inputs>();
  const { uploadImage, clearUploadedImage, uploadedImage, setUploadedImage } =
    useUploadImage();
  /** State */
  const [submitted, setSubmitted] = useState<boolean>(false);
  const [backgroundColor, setBackgroundColor] = useState<string>("");
  const [textColor, setTextColor] = useState<string>("");
  const [colorValue, setColorValue] = useState<string>("");
  const [showBgColorPicker, setShowBgColorPicker] = useState<boolean>(false);
  const [showTxtColorPicker, setShowTxtColorPicker] = useState<boolean>(false);
  const [image, setImage] = useState<File | null>(null);

  /** Callbacks */
  const goBack = () => navigate(-1);
  const onSubmit = (data: Inputs) => {
    if (backgroundColor === "") {
      setAlert({
        isOpen: true,
        message: "Please add background color.",
        severity: "error",
      });
      setSubmitted(true);
      return;
    }
    if (textColor === "") {
      setAlert({
        isOpen: true,
        message: "Please add text color.",
        severity: "error",
      });
      setSubmitted(true);
      return;
    }
    if (!uploadedImage) {
      setAlert({
        isOpen: true,
        message: "Please upload an image.",
        severity: "error",
      });
      setSubmitted(true);
      return;
    }
    setSubmitted(false);
    const body: any = {
      ...data,
      colors: [backgroundColor, textColor],
      icon: uploadedImage,
    };
    updateBadge(params?.id as string, body);
  };

  const onBgColorChange = (color: any) => setColorValue(color.hex);
  const onBgColorChangeComplete = (color: any) => {
    setBackgroundColor(color.hex);
    setShowBgColorPicker(false);
  };
  const onTxtColorChange = (color: any) => setColorValue(color.hex);
  const onTxtColorChangeComplete = (color: any) => {
    setTextColor(color.hex);
    setShowTxtColorPicker(false);
  };

  /** Effects */
  useEffect(() => {
    if (params?.id) {
      getOneBadge(params?.id);
    }
  }, [params?.id]);

  useEffect(() => {
    if (oneBadge) {
      reset({
        name: oneBadge?.name,
        description: oneBadge?.description,
        minDrops: oneBadge?.minDrops,
        maxDrops: oneBadge?.maxDrops,
        type: oneBadge?.type,
      });
      setUploadedImage(oneBadge?.icon as string);
      setBackgroundColor((oneBadge?.colors?.[0] ?? "") as string);
      setTextColor((oneBadge?.colors?.[1] ?? "") as string);
    }
  }, [oneBadge]);

  useEffect(() => {
    getAllMarketPrices();
    return () => {
      clearUploadedImage();
    };
  }, []);

  useEffect(() => {
    if (updatedBadge) {
      navigate(-1);
      clearUpdatedBadge();
    }
  }, [updatedBadge]);

  const bgColorError =
    (Object.values(errors)?.length && backgroundColor === "") ||
    (submitted && backgroundColor === "");

  const txtColorError =
    (Object.values(errors)?.length && textColor === "") ||
    (submitted && textColor === "");

  const onImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e?.target?.files) {
      setImage(e.target.files[0]);
    }
  };

  const onUploadImage = () => {
    if (!image) return;
    uploadImage(image);
  };

  const imageError = submitted && !image;

  return (
    <div>
      <div className="flex flex-row items-center mb-6">
        <h1 className="font-satoshi text-2xl font-bold text-dark">
          Edit badge
        </h1>
      </div>
      <div className="flex flex-col sm:flex-row">
        <div className="w-full sm:w-1/2 bg-white border border-gray-border drop-shadow-md mb-1 sm:mb-0">
          <div className="w-full border-b border-gray-border px-[28px] py-[15px]">
            <p className="font-satoshi font-medium text-base text-dark">
              Edit badge
            </p>
          </div>
          <form onSubmit={handleSubmit(onSubmit)} className="w-full px-7 py-7">
            {/* Name */}
            <ControlledInput
              name="name"
              rules={{ required: true }}
              control={control}
              error={Boolean(errors?.name)}
              label="Name"
              type="text"
              placeholder="Enter name here"
            />
            {/* Description */}
            <ControlledInput
              name="description"
              rules={{ required: true }}
              control={control}
              error={Boolean(errors?.description)}
              label="Description"
              type="text"
              placeholder="Enter description here"
            />
            {/* Min Drops */}
            <ControlledInput
              name="minDrops"
              rules={{ required: true }}
              control={control}
              error={Boolean(errors?.minDrops)}
              label="Min Drops"
              type="number"
              placeholder="Enter min drops here"
            />
            {/* Max Drops */}
            <ControlledInput
              name="maxDrops"
              rules={{ required: true }}
              control={control}
              error={Boolean(errors?.maxDrops)}
              label="Max Drops"
              type="number"
              placeholder="Enter max drops here"
            />
            <ControlledSelect
              name="type"
              control={control}
              rules={{ required: true }}
              error={Boolean(errors?.type)}
              label="Composition type"
              options={marketPrices?.map((price) => ({
                _id: price?._id as string,
                title: price?.name as string,
              }))}
              placeholder="Pick composition here"
            />
            {/* Background Color */}
            <div className="mb-4">
              <p className={`font-satoshi text-dark text-sm font-medium mb-3`}>
                Add background color
              </p>
              <div
                className={`w-full h-[46px] px-4 bg-sky-lighter border ${
                  bgColorError ? "border-red-base" : "border-gray-border"
                } ${
                  bgColorError
                    ? "focus:border-red-base"
                    : "focus:border-focused-border"
                } rounded font-satoshi text-base text-dark font-medium outline-none flex flex-row items-center justify-between`}
              >
                <div className="flex flex-row items-center w-[100%] h-[100%]">
                  {backgroundColor !== "" ? (
                    <div
                      onClick={() => {
                        setBackgroundColor("");
                        setShowBgColorPicker(false);
                      }}
                      style={{ backgroundColor }}
                      className={`w-[15px] h-[15px] mr-1 cursor-pointer rounded`}
                    />
                  ) : null}
                </div>
                <button
                  onClick={() => setShowBgColorPicker(!showBgColorPicker)}
                  type="button"
                  className="px-[8px] py-[3px] bg-primary-base font-satoshi font-medium text-white text-sm rounded"
                >
                  {backgroundColor !== "" ? "Edit" : "Add"}
                </button>
              </div>
            </div>
            {showBgColorPicker ? (
              <SketchPicker
                color={colorValue}
                onChangeComplete={onBgColorChangeComplete}
                onChange={onBgColorChange}
              />
            ) : null}
            {/* Text color */}
            <div className="mb-4">
              <p className={`font-satoshi text-dark text-sm font-medium mb-3`}>
                Add text colors
              </p>
              <div
                className={`w-full h-[46px] px-4 bg-sky-lighter border ${
                  txtColorError ? "border-red-base" : "border-gray-border"
                } ${
                  txtColorError
                    ? "focus:border-red-base"
                    : "focus:border-focused-border"
                } rounded font-satoshi text-base text-dark font-medium outline-none flex flex-row items-center justify-between`}
              >
                <div className="flex flex-row items-center w-[100%] h-[100%]">
                  {textColor !== "" ? (
                    <div
                      onClick={() => {
                        setTextColor("");
                        setShowBgColorPicker(false);
                      }}
                      style={{ backgroundColor: textColor }}
                      className={`w-[15px] h-[15px] mr-1 cursor-pointer rounded`}
                    />
                  ) : null}
                </div>
                <button
                  onClick={() => setShowTxtColorPicker(!showTxtColorPicker)}
                  type="button"
                  className="px-[8px] py-[3px] bg-primary-base font-satoshi font-medium text-white text-sm rounded"
                >
                  {textColor !== "" ? "Edit" : "Add"}
                </button>
              </div>
            </div>
            {showTxtColorPicker ? (
              <SketchPicker
                color={colorValue}
                onChangeComplete={onTxtColorChangeComplete}
                onChange={onTxtColorChange}
              />
            ) : null}
            <div className="flex flex-row items-center justify-end">
              <SecondaryButton type="button" onClick={goBack} label="Cancel" />
              <PrimaryButton type="submit" className="ml-4" label="Save" />
            </div>
          </form>
        </div>
        <div
          className={`w-full sm:w-1/3 sm:ml-8 bg-white border border-gray-border drop-shadow-md h-[360px]`}
        >
          <div className="w-full border-b border-gray-border px-[28px] py-[15px]">
            <p className="font-satoshi font-medium text-base text-dark">
              Article photo
            </p>
          </div>
          <div className="w-full px-7 py-7">
            <FormUpload
              onChange={onImageChange}
              value={image}
              error={imageError}
              imageUrl={uploadedImage as string}
            />
            <div className="flex flex-row items-center justify-end">
              <SecondaryButton onClick={goBack} label="Cancel" />
              <PrimaryButton
                onClick={onUploadImage}
                className="ml-4"
                label="Save"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default withProtectedRoute(EditBadgeScreen);
